import React, { useState, useEffect, useCallback } from "react";
import { Plus, RefreshCcw, Eye, Play, Archive, Edit3 } from "lucide-react";
import { Link } from "react-router-dom";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CfgStatusBadge, CfgEnvBadge, ToggleDot } from "./configurations/CfgBadges";
import ConfigurationFormDialog from "./configurations/ConfigurationFormDialog";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function PlatformConfigurations() {
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [dialog, setDialog] = useState({ open: false, existing: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgs, ts] = await Promise.all([
        api.get("/platform/configurations"),
        api.get("/platform/tenants"),
      ]);
      setItems(cfgs.data.items || []);
      setTenants(ts.data.items || []);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const action = async (id, verb) => {
    setBusyId(id + verb);
    try {
      await api.post(`/platform/configurations/${id}/${verb}`, {});
      toast.success(`Config ${verb} berhasil.`);
      load();
    } catch (e) { notifyError(e, `Gagal ${verb}`); }
    finally { setBusyId(null); }
  };

  const tenantOf = (id) => tenants.find((t) => t.tenant_id === id);

  return (
    <PlatformLayout title="Configuration">
      <div data-testid="platform-configurations-page">
        <div className="flex items-center gap-3 mb-4">
          <div className="text-xs text-white/50">Menampilkan {items.length} configuration</div>
          <Button onClick={load} variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/10 ml-auto">
            <RefreshCcw size={14} className="mr-1" /> Refresh
          </Button>
          <Button onClick={() => setDialog({ open: true, existing: null })} className="bg-white text-black hover:bg-white/90" data-testid="btn-create-cfg">
            <Plus size={14} className="mr-1" /> Buat Configuration
          </Button>
        </div>

        {loading ? (
          <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
        ) : items.length === 0 ? (
          <div className="text-white/40 text-sm p-8 rounded-2xl border border-white/10 text-center">
            Belum ada configuration. Klik <strong className="text-white">+ Buat Configuration</strong>.
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
              <div className="col-span-3">Tenant</div>
              <div className="col-span-1">Env</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1">Version</div>
              <div className="col-span-1">Timezone</div>
              <div className="col-span-1">Currency</div>
              <div className="col-span-2">Feat (E·P·S)</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            {items.map((cfg) => {
              const t = tenantOf(cfg.tenant_id);
              const canActivate = cfg.status === "DRAFT";
              const canEdit = cfg.status !== "ARCHIVED";
              const canArchive = cfg.status !== "ARCHIVED";
              return (
                <div key={cfg.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-t border-white/5 items-center"
                  data-testid={`cfg-row-${cfg.id}`}>
                  <div className="col-span-3 min-w-0">
                    <Link to={`/platform/configurations/${cfg.id}`}
                      className="text-sm font-bold text-white hover:text-emerald-300 truncate block"
                      data-testid={`cfg-link-${cfg.id}`}>
                      {t?.tenant_name || cfg.tenant_id.slice(0, 8)}
                    </Link>
                    <div className="text-[10px] text-white/40 font-mono truncate">{t?.tenant_code || "—"}</div>
                  </div>
                  <div className="col-span-1"><CfgEnvBadge environment={cfg.environment} size="sm" /></div>
                  <div className="col-span-1"><CfgStatusBadge status={cfg.status} size="sm" /></div>
                  <div className="col-span-1 text-xs text-white/70 font-mono">v{cfg.config_version}</div>
                  <div className="col-span-1 text-xs text-white/60 font-mono truncate">{cfg.localization?.timezone || "—"}</div>
                  <div className="col-span-1 text-xs text-white/60 font-mono">{cfg.currency?.currency_code || "—"}</div>
                  <div className="col-span-2 flex items-center gap-2 text-[10px]">
                    <ToggleDot on={!!cfg.email?.enabled} />
                    <ToggleDot on={!!cfg.payment?.enabled} />
                    <ToggleDot on={!!cfg.shipping?.enabled} />
                  </div>
                  <div className="col-span-2 flex gap-1 justify-end">
                    <Link to={`/platform/configurations/${cfg.id}`}
                      className="w-7 h-7 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10"
                      title="View" data-testid={`cfg-view-${cfg.id}`}>
                      <Eye size={12} />
                    </Link>
                    {canEdit && (
                      <button onClick={() => setDialog({ open: true, existing: cfg })}
                        className="w-7 h-7 rounded-lg grid place-items-center text-sky-400 hover:bg-sky-500/10"
                        title="Edit" data-testid={`cfg-edit-${cfg.id}`}>
                        <Edit3 size={12} />
                      </button>
                    )}
                    {canActivate && (
                      <button onClick={() => action(cfg.id, "activate")} disabled={busyId === cfg.id + "activate"}
                        className="w-7 h-7 rounded-lg grid place-items-center text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
                        title="Activate" data-testid={`cfg-activate-${cfg.id}`}>
                        <Play size={12} />
                      </button>
                    )}
                    {canArchive && (
                      <button onClick={() => action(cfg.id, "archive")} disabled={busyId === cfg.id + "archive"}
                        className="w-7 h-7 rounded-lg grid place-items-center text-neutral-400 hover:bg-neutral-500/10 disabled:opacity-50"
                        title="Archive" data-testid={`cfg-archive-${cfg.id}`}>
                        <Archive size={12} />
                      </button>
                    )}
                  </div>
                  <div className="col-span-12 md:hidden text-[10px] text-white/40 mt-1">Updated {fmt(cfg.updated_at)}</div>
                </div>
              );
            })}
          </div>
        )}

        <ConfigurationFormDialog
          tenants={tenants}
          existing={dialog.existing}
          open={dialog.open}
          onClose={() => setDialog({ open: false, existing: null })}
          onSaved={load}
        />
      </div>
    </PlatformLayout>
  );
}
